/**
 * Modal/Bottom Sheet - Apple-style clean design with focus trap
 * Performance optimized with React.memo
 * Portals to #portal-root for correct stacking context
 */

import React, { useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { triggerHaptic } from '@/lib/haptics';
import { useReducedMotion, useVisualViewport, getPortalRoot } from '@/lib/hooks';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  showClose?: boolean;
  ariaDescription?: string;
  /** Full bleed variant - no padding, full-width content */
  fullBleed?: boolean;
  /** Size variant - sm for confirmation dialogs, md (default), lg for complex forms */
  size?: 'sm' | 'md' | 'lg';
}

export const Modal = memo(function Modal({
  isOpen,
  onClose,
  children,
  title,
  showClose = true,
  ariaDescription,
  fullBleed = false,
  size = 'md',
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const { isKeyboardVisible } = useVisualViewport();

  // Size classes for desktop
  const sizeClasses = {
    sm: 'sm:min-w-[320px] sm:max-w-sm',
    md: 'sm:min-w-[380px] sm:max-w-md',
    lg: 'sm:min-w-[480px] sm:max-w-lg',
  };

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        triggerHaptic();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Focus management and focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    // Store previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Get all focusable elements
    const getFocusableElements = () => {
      if (!modalRef.current) return [];
      return Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled'));
    };

    // Focus first element
    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];
    setTimeout(() => firstElement?.focus(), 100);

    // Focus trap handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const elements = getFocusableElements();
      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    triggerHaptic();
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Animation classes based on reduced motion preference
  const backdropAnimation = prefersReducedMotion ? '' : 'animate-backdrop-fade';
  const modalAnimation = prefersReducedMotion ? '' : 'animate-slide-up-sheet sm:animate-fade-in-scale';

  // Calculate visible height for keyboard scenarios
  const visibleHeight = window.visualViewport?.height || window.innerHeight;

  const content = (
    <div
      className="fixed inset-0 z-50 flex justify-center items-end sm:items-center motion-safe:transition-all duration-200"
      style={{
        // Constrain to visual viewport when keyboard is visible
        ...(isKeyboardVisible && {
          position: 'fixed',
          top: window.visualViewport?.offsetTop || 0,
          left: 0,
          right: 0,
          height: visibleHeight,
        }),
      }}
    >
      {/* Backdrop - Refined blur with subtle fade, touch-action:none prevents iOS scroll-through */}
      <div
        className={`fixed inset-0 bg-black/35 backdrop-blur-[12px] motion-safe:transition-opacity ${backdropAnimation}`}
        style={{ touchAction: 'none' }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal - Refined glass surface with Apple HIG 2026 depth */}
      <div
        ref={modalRef}
        className={`relative w-full sm:w-auto ${sizeClasses[size]} overflow-hidden flex flex-col
                    bg-white/[0.98] backdrop-blur-xl backdrop-saturate-[1.4]
                    border border-white/30 sm:border-black/[0.04]
                    shadow-[0_-2px_8px_rgba(0,0,0,0.03),0_8px_24px_rgba(0,0,0,0.08),0_24px_48px_rgba(0,0,0,0.08)]
                    sm:shadow-[0_0.5px_1px_rgba(0,0,0,0.02),0_4px_16px_rgba(0,0,0,0.08),0_16px_48px_rgba(0,0,0,0.1)]
                    rounded-t-[28px] sm:rounded-[22px]
                    ${modalAnimation}`}
        style={{
          // Limit max height - more aggressive when keyboard visible, leave room for browser UI
          // Use 100dvh for iOS Safari address bar awareness
          maxHeight: isKeyboardVisible ? `${visibleHeight - 20}px` : 'min(85dvh, calc(100dvh - 80px))',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={ariaDescription ? 'modal-description' : undefined}
        tabIndex={-1}
      >
        {/* Handle bar for mobile - refined swipe indicator */}
        {!fullBleed && (
          <div className="flex justify-center pt-3 pb-2 sm:hidden">
            <div className="w-9 h-[5px] bg-gray-300/50 rounded-full" />
          </div>
        )}

        {/* Header - Refined Apple-style with proper touch targets */}
        {/* Only show header if title has content (not empty string) or showClose is true and not fullBleed */}
        {((title && title !== '') || (showClose && !fullBleed)) && (
          <header className="flex items-center justify-between py-3 border-b border-gray-100/80 px-5">
            {title && title !== '' && (
              <h2 id="modal-title" className="text-body-lg font-semibold text-gray-900 tracking-[-0.015em]">
                {title}
              </h2>
            )}
            {showClose && !fullBleed && (
              <button
                onClick={handleClose}
                className="w-11 h-11 min-w-[44px] min-h-[44px] -mr-2 rounded-xl flex items-center justify-center
                           text-gray-400 hover:text-gray-500 hover:bg-gray-100/60 active:bg-gray-100/80
                           active:scale-[0.96] motion-safe:transition-all motion-safe:duration-150
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200/60 focus-visible:ring-offset-2
                           touch-manipulation"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </header>
        )}

        {ariaDescription && <p id="modal-description" className="sr-only">{ariaDescription}</p>}

        {/* Content - with safe area padding for mobile, overscroll containment */}
        <div
          className={`flex-1 overflow-y-auto overflow-x-hidden overscroll-contain momentum-scroll ${fullBleed ? '' : 'px-5 py-4'}`}
          style={fullBleed ? undefined : {
            paddingBottom: `max(env(safe-area-inset-bottom, 16px), 16px)`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(content, getPortalRoot());
});

/**
 * Confirmation dialog - Apple-style alert
 */
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  variant?: 'default' | 'danger';
}

export const ConfirmModal = memo(function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  variant = 'default',
}: ConfirmModalProps) {
  const handleConfirm = useCallback(() => {
    triggerHaptic();
    onConfirm();
  }, [onConfirm]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} ariaDescription={message} size="sm">
      <div className="pb-2">
        <p className="text-muted text-body leading-relaxed mb-6">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 min-h-[48px] rounded-[14px]
                       bg-gray-100/70 border border-gray-200/50
                       text-gray-700 font-semibold tracking-[-0.01em]
                       hover:bg-gray-100/90 hover:border-gray-200/70
                       active:scale-[0.98] motion-safe:transition-all duration-150 touch-manipulation"
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-3 min-h-[48px] rounded-[14px]
                        text-white font-semibold tracking-[-0.01em]
                        motion-safe:transition-all duration-150 active:scale-[0.98]
                        disabled:opacity-50 touch-manipulation
                        shadow-[0_0.5px_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(0,0,0,0.08),0_3px_8px_rgba(0,0,0,0.08),inset_0_0.5px_0_rgba(255,255,255,0.2)]
                        hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.12)] hover:-translate-y-px
                        ${variant === 'danger'
                          ? 'bg-gradient-to-b from-red-500 via-red-500 to-red-600'
                          : 'bg-gradient-to-b from-[#0A84FF] via-primary-500 to-primary-600'
                        }`}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Loading...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
});

